// Implements: F-IDN-027, F-UIX-018, F-UIX-010
/**
 * One team of Equipos (mockup v3 P3, `vTeam`). Mount it keyed by the team's id (checklist 14):
 * switching teams mounts a fresh container, so the previous team's members, dialogs or late
 * answers never show on the next one.
 *
 * The team is read from the organization's team list (the server lists only what the reader may
 * see), so a team that is not there, deleted, foreign or invented, shows "No encontramos este
 * equipo". Renaming and deleting are offered only when the ORGANIZATION's scope carries
 * `team:create` (C8: a team lead may not rename their team); members, their team role (the leader,
 * C4) and adding people follow the server's answers. Adding is offered only when the reader may
 * list the organization's people.
 *
 * One leader per team (P3-3, owner 2026-10-19: ask the person, then do what they choose). Adding
 * or promoting somebody as Líder while another person leads first asks "Este equipo ya tiene líder
 * (<name>)": Cancelar (the initial focus) sends nothing; "Convertir a <name> en miembro" sends the
 * change with `replace_lead_user_id` naming that lead, and the server demotes them in the same
 * transaction only while they still lead. A `409 lead_taken` (stale list, or the lead changed after
 * the question) re-reads the team and asks again, naming the lead the server now holds.
 */

import { useState } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate } from "react-router";

import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { PageHeader } from "../../../shared/ui/molecules/PageHeader";
import type { BackofficeScope } from "../../../types/api";
import type { TeamRole, TeamView } from "../../../types/organization";
import { NameDialog } from "../components/NameDialog";
import styles from "../components/Organization.module.css";
import { TeamMembersTable } from "../components/TeamMembersTable";
import { usePeopleList, useTeamList } from "../hooks/usePeople";
import {
  useAddTeamMember,
  useDeleteTeam,
  useRemoveTeamMember,
  useRenameTeam,
  useSetTeamMemberRole,
} from "../hooks/useTeams";
import { allowsAtOrganization } from "../model/targets";
import { TEAMS_PATH, writeProblem } from "./PeopleContainer";

/** Whom a seat change is for: a person being added, or a member whose role changes. */
interface SeatTarget {
  readonly kind: "add" | "role";
  readonly userId: string;
  /** Their name, for the question "Si continúas, <person> será el líder…". */
  readonly person: string;
}

/** The question on screen: whose seat, and the lead it names (the one a hand-over demotes). */
interface LeadChoice {
  readonly target: SeatTarget;
  readonly lead: { readonly id: string; readonly name: string };
}

/** The lead of `team` other than `userId`, if any. */
function leadBesides(team: TeamView, userId: string): LeadChoice["lead"] | null {
  const lead = team.members.find((member) => member.role === "team_lead" && member.user.id !== userId);
  return lead === undefined ? null : { id: lead.user.id, name: lead.user.full_name };
}

/** The server's one-leader refusal (`409 {field: "team_role", reason: "lead_taken"}`). */
function isLeadTaken(error: unknown): boolean {
  const details = asApiError(error).details;
  return details?.field === "team_role" && details.reason === "lead_taken";
}

export interface TeamDetailContainerProps {
  readonly organizationId: string;
  readonly teamId: string;
  readonly scopes: readonly BackofficeScope[];
}

export function TeamDetailContainer({ organizationId, teamId, scopes }: TeamDetailContainerProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teams = useTeamList(organizationId);
  const people = usePeopleList(organizationId);
  const rename = useRenameTeam(organizationId, teamId);
  const remove = useDeleteTeam(organizationId, teamId);
  const addMember = useAddTeamMember(organizationId, teamId);
  const setRole = useSetTeamMemberRole(organizationId, teamId);
  const removeMember = useRemoveTeamMember(organizationId, teamId);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [renameProblem, setRenameProblem] = useState<string | null>(null);
  const [choice, setChoice] = useState<LeadChoice | null>(null);
  const canManage = allowsAtOrganization(scopes, organizationId, "team:create");

  const refused = (error: unknown): void => {
    setProblem(writeProblem(asApiError(error), t("common.state.error")));
  };

  if (teams.isError) {
    return (
      <ErrorState
        error={asApiError(teams.error)}
        onRetry={() => {
          void teams.refetch();
        }}
      />
    );
  }
  if (teams.data === undefined) return <LoadingState label={t("organization.teams.loading")} />;
  const team = teams.data.teams.find((candidate) => candidate.id === teamId);
  if (team === undefined) {
    return (
      <EmptyState
        icon="users"
        title={t("organization.team.notFound.title")}
        body={t("organization.team.notFound.body")}
        action={
          <Link to={TEAMS_PATH} className={buttonClassName("primary")}>
            {t("organization.team.back")}
          </Link>
        }
      />
    );
  }

  const members = new Set(team.members.map((member) => member.user.id));
  const candidates =
    people.data === undefined
      ? null
      : people.data.people.filter((person) => !members.has(person.user.id)).map((person) => person.user);
  const busy = addMember.isPending || setRole.isPending || removeMember.isPending;
  const handingOver = addMember.isPending || setRole.isPending;

  /**
   * Send one seat change. A `409 lead_taken` means the lead is not the one this screen knew (a stale
   * list, or another admin changed it after the question): re-read the team and ask again, naming
   * the lead the server now holds; if the re-read shows none, say what the server said.
   */
  const send = (target: SeatTarget, role: TeamRole, replaceLeadUserId?: string): void => {
    const mutation = target.kind === "add" ? addMember : setRole;
    if (mutation.isPending) return;
    mutation.mutate(
      { userId: target.userId, role, ...(replaceLeadUserId === undefined ? {} : { replaceLeadUserId }) },
      {
        onSuccess: () => {
          setChoice(null);
        },
        onError: (error) => {
          setChoice(null);
          if (!isLeadTaken(error)) {
            refused(error);
            return;
          }
          void teams.refetch().then((reread) => {
            const fresh = reread.data?.teams.find((candidate) => candidate.id === teamId);
            const lead = fresh === undefined ? null : leadBesides(fresh, target.userId);
            if (lead === null) refused(error);
            else setChoice({ target, lead });
          });
        },
      },
    );
  };

  // Owner 2026-10-19: when the team already has a lead, ask before anything is sent. The question
  // keeps the lead it named, so a re-read while it is open never drops the person's click.
  const seat = (target: SeatTarget, role: TeamRole): void => {
    setProblem(null);
    const lead = role === "team_lead" ? leadBesides(team, target.userId) : null;
    if (lead !== null) {
      setChoice({ target, lead });
      return;
    }
    send(target, role);
  };
  const size =
    team.members.length === 1
      ? t("organization.teams.oneMember")
      : t("organization.teams.members", { count: team.members.length });

  return (
    <section className={styles.page}>
      <PageHeader
        title={team.name}
        subtitle={size}
        aside={
          canManage ? (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                hasPopup="dialog"
                onClick={() => {
                  setRenameProblem(null);
                  setRenaming(true);
                }}
              >
                {t("organization.teams.rename")}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleting(true);
                }}
              >
                {t("organization.teams.delete")}
              </Button>
            </div>
          ) : undefined
        }
      />
      {problem === null ? null : (
        <p className={styles.problem} role="alert">
          {problem}
        </p>
      )}
      <section className={styles.section} aria-labelledby={`team-members-${team.id}`}>
        <div className={styles.sectionHead}>
          <h2 id={`team-members-${team.id}`} className={styles.sectionTitle}>
            {t("organization.team.membersTitle")}
          </h2>
          <span className={styles.spacer} />
          <span className={styles.meta}>{t("organization.team.leadNote")}</span>
        </div>
        <TeamMembersTable
          team={team}
          candidates={candidates}
          busy={busy}
          onRoleChange={(userId: string, role: TeamRole) => {
            const person = team.members.find((member) => member.user.id === userId)?.user.full_name ?? "";
            seat({ kind: "role", userId, person }, role);
          }}
          onRemove={(userId) => {
            setProblem(null);
            removeMember.mutate(userId, { onError: refused });
          }}
          onAdd={(userId, role) => {
            const person = candidates?.find((user) => user.id === userId)?.full_name ?? "";
            seat({ kind: "add", userId, person }, role);
          }}
        />
      </section>
      {renaming ? (
        <NameDialog
          title={t("organization.teams.renameTitle")}
          label={t("organization.teams.nameLabel")}
          submitLabel={t("organization.teams.save")}
          initial={team.name}
          pending={rename.isPending}
          problem={renameProblem}
          onSubmit={(name) => {
            rename.mutate(name, {
              onSuccess: () => {
                setRenaming(false);
              },
              onError: (error) => {
                setRenameProblem(writeProblem(asApiError(error), t("common.state.error")));
              },
            });
          }}
          onCancel={() => {
            setRenaming(false);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={choice !== null}
        title={t("organization.team.leadTaken.title", { name: choice?.lead.name ?? "" })}
        body={t("organization.team.leadTaken.body", { person: choice?.target.person ?? "", name: choice?.lead.name ?? "" })}
        confirmLabel={t("organization.team.leadTaken.convert", { name: choice?.lead.name ?? "" })}
        cancelLabel={t("common.action.cancel")}
        confirmLoading={handingOver}
        onConfirm={() => {
          if (choice !== null) send(choice.target, "team_lead", choice.lead.id);
        }}
        onCancel={() => {
          // While the hand-over is on its way the question stays until the answer (verifier round 1).
          if (!handingOver) setChoice(null);
        }}
      />
      <ConfirmDialog
        open={deleting}
        title={t("organization.teams.delete.title", { name: team.name })}
        body={t("organization.teams.delete.body")}
        confirmLabel={t("organization.teams.delete.confirm")}
        cancelLabel={t("common.action.cancel")}
        confirmLoading={remove.isPending}
        onConfirm={() => {
          if (remove.isPending) return;
          remove.mutate(undefined, {
            onSuccess: () => {
              setDeleting(false);
              void navigate(TEAMS_PATH);
            },
            onError: (error) => {
              setDeleting(false);
              refused(error);
            },
          });
        }}
        onCancel={() => {
          setDeleting(false);
        }}
      />
    </section>
  );
}
